/**
 * login.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

//import bluebird from "bluebird";
import moment from "moment-timezone";
import * as React from "react";
import { Link, Navigate, useLocation } from "react-router-dom";

import FingerprintIcon from "@mui/icons-material/Fingerprint";
//import MenuItem from "@mui/material/MenuItem";
//import Select, { SelectChangeEvent } from "@mui/material/Select";
import { Alert, Button, FormControl, IconButton, InputAdornment, InputLabel, 
  OutlinedInput, TextField } from "@mui/material";

import { Dialog } from "../components/dialog.component";

import { AuthType, ThirdPartyAuth } from "../models/auth";
import { AuthContext } from "./auth.context";
import { authServiceFactory, ChallengeResponse } from "../services/auth.service";

interface LoginFormProps {
  ref?: React.RefObject<LoginForm | null>,
  hideLogin?: boolean,
  onFinish(): Promise<void>
}

interface LoginFormState {
  sending: boolean,
  //region: string,
  email: string,
  password: string,
  otp: string,
  redirectPath: string,
  errMsg: string,
  buttonText: string,
  errors: {
    email: string,
    password: string
  },
  authenticators: Array<ThirdPartyAuth>,
  showLocalWebauthn: boolean,
  showOTP: boolean
}

export class LoginForm extends React.Component<LoginFormProps, LoginFormState> {

  private passInput: HTMLInputElement | undefined;
  private otpInput: HTMLInputElement | undefined;
  private challenge: ChallengeResponse | undefined;

  private emailFocused: boolean;

  constructor(props: LoginFormProps) {
    super(props);
    //const authService = authServiceFactory();
    this.challenge = undefined;
    this.emailFocused = false;

    this.doWebAuthn = this.doWebAuthn.bind(this);
    this.doLogin = this.doLogin.bind(this);
    this.updateValue = this.updateValue.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onFocusEmail = this.onFocusEmail.bind(this);
    this.onBlurEmail = this.onBlurEmail.bind(this);
    //this.onRegionChange = this.onRegionChange.bind(this);

    this.state = {
      sending: false,
      //region: authService.region?.id || "",
      buttonText: "Login",
      email: "",
      password: "",
      otp: "",
      redirectPath: "",
      errMsg: "",
      errors: {
        email: "",
        password: ""
      },
      authenticators: [],
      showLocalWebauthn: false,
      showOTP: false
    };
  }

  componentDidMount (): void {
    this.challenge = undefined;
  }

  private updateValue(event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    const newValue = propName === "email" ? inputElem.value?.toLowerCase() :
      inputElem.value;
    const update: any = {[propName]: newValue};
    this.setState(update);
    if (propName === "email" && !this.emailFocused) {
      this.fetchAuthOptions(newValue);
    }
  }

  private onKeyUp(event: React.KeyboardEvent): void {
    if (event.key !== "Enter") { return; }
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    if (propName === "email") {
      this.passInput?.focus();
    } else if (propName === "password" && this.state.showOTP) {
      this.otpInput?.focus();
    } else {
      this.doLogin();
    }
  }

  private onFocusEmail() {
    this.emailFocused = true;
  }
  private async onBlurEmail() {
    this.emailFocused = false;
    this.fetchAuthOptions(this.state.email);
  }

  private async fetchAuthOptions(email: string | undefined) {
    if (this.state.sending) { return; }
    if (!email) {
      this.setState({errors: {...this.state.errors, email: "Please, enter a valid email"}});
      return;
    }
    const authService = authServiceFactory();
    try {
      this.setState({sending: true});
      this.challenge = await authService.getAuthOptions(email);

      //let havePortableSecurityKey = false;
      const update: any = {sending: false, showLocalWebauthn: false, showOTP: false, authenticators: []};
      for (const auth of this.challenge.auths) {
        if (auth.idtype === AuthType.webauthn) {
          update.showLocalWebauthn = true;
          update.authenticators.push(auth);
        } else if (auth.idtype === AuthType.googAuth || auth.idtype === AuthType.totp) {
          update.showOTP = true;
        }
      }
      this.setState(update);
    } catch (err) {
      console.error("Error getting auth options:", err);
      if ((err as Error).message === "User not found") {
        this.setState({sending: false});
      } else {
        this.setState({errMsg: (err as Error).message, sending: false});
      }
    }
  }

  /*private onRegionChange(event: SelectChangeEvent<string>): void {
    const authService = authServiceFactory();
    const regionKey = event.target.value as string;

    this.setState({region: regionKey});
    authService.region = authService.regions[regionKey];
  }*/

  public async doWebAuthn(): Promise<void> {
    if (this.state.sending) { return; }
    const authService = authServiceFactory();

    if (!this.challenge) {
      this.setState({sending: false, errMsg: "No Information received from server!"});
      return;
    }

    /*const webAuthOptions = local ?
      this.challenge.auths.filter(
        a => a.idtype === AuthType.webauthn && a.attachment === "platform") :
        this.challenge.auths.filter(
          a => a.idtype === AuthType.webauthn && a.attachment === "cross-platform");*/

    try {
      await authService.assertLocalCredential(this.state.authenticators,
        this.challenge.challenge, this.challenge.rp.id, this.challenge.token);

      await this.finalizeLogin();
      return; // don't do state update here
    } catch (err) {
      if ((err as Error).name === "NotAllowedError") {
        console.error("NotAllowedError error");
        this.setState({
          errMsg: "This device's authenticator may not be set up for your account."
        });
      } else {
        console.error("Error logging in with webauthn:", err);
        this.setState({errMsg: (err as Error).message});
      }
    }

    this.setState({sending: false});
  }

  public async doLogin(): Promise<void> {
    if (this.state.sending) { return; }
    const authService = authServiceFactory();

    try {
      this.setState({sending: true})
      await authService.login(this.state.email, this.state.password, this.state.otp);
      await this.finalizeLogin();
    } catch (err) {
      console.error("Error logging in:", err);
      this.setState({errMsg: (err as Error).message, sending: false});
    }
  }

  private async finalizeLogin() {
    await this.props.onFinish();
  }

  public canSubmit(): boolean {
    if (this.state.sending) { return false; }
    if (this.challenge) {
      return this.state.email as any && this.state.password as any;
    } else {
      return this.state.email as any;
    }
  }

  /*
  const authService = authServiceFactory();
  const regions = authService.regions || {};
  <div className="form-row">
      <FormControl fullWidth>
        <InputLabel>Region</InputLabel>
        <Select fullWidth onChange={this.onRegionChange} value={this.state.region} label="Region">
          {Object.keys(regions).map(key =>
              <MenuItem key={key} value={key}>{regions[key].region_name}</MenuItem>)}
        </Select>
      </FormControl>
      </div>*/
  render(): React.ReactElement<any, any> {
    return <form className="form-wrapper">
      { this.state.errMsg && <Alert
        variant="filled"
        severity="error">{this.state.errMsg}</Alert>}
      <div className="form-row">
      <FormControl fullWidth>
        <TextField id="email-input"
          label="Email" type="email" name="email" required
          variant="outlined"
          fullWidth
          autoComplete="username webauthn"
          slotProps={{
            htmlInput: {"data-testid": "email-input"}
          }}
          value={this.state.email}
          onChange={this.updateValue}
          onKeyUp={this.onKeyUp}
          onFocus={this.onFocusEmail}
          onBlur={this.onBlurEmail} />
      </FormControl>
      </div>
      <div className="form-row">
      <FormControl fullWidth variant="outlined">
        <InputLabel htmlFor="pass-input">Password</InputLabel>
        <OutlinedInput
          id="pass-input"
          type="password"
          name="password"
          label="Password"
          autoComplete="current-password"
          required
          inputProps={{"data-testid": "pass-input"}}
          inputRef={(input: any) => {this.passInput = input; }}
          value={this.state.password}
          onChange={this.updateValue}
          onKeyUp={this.onKeyUp}
          endAdornment={
            this.state.showLocalWebauthn && <InputAdornment position="end">
              <IconButton aria-label="web authentication" onClick={() => this.doWebAuthn()}>
                <FingerprintIcon /></IconButton>
            </InputAdornment>
          }/>
      </FormControl>
      </div>
      { this.state.showOTP && <div className="form-row">
      <FormControl fullWidth>
        <TextField id="otp-input" label="MFA Code" type="text" name="otp" required
          data-prop-name="otp"
          variant="outlined"
          autoComplete="one-time-code"
          slotProps={{
            htmlInput: {"data-testid": "otp-input"}
          }}
          inputRef={input => { this.otpInput = input; } }
          value={this.state.otp} onChange={this.updateValue} onKeyPress={this.onKeyUp} />
      </FormControl>
      </div>}
      {!this.props.hideLogin && <div className="button-row">
        <Button color="primary" variant="contained"
            disableRipple disableTouchRipple
            disabled={!this.canSubmit()} onClick={this.doLogin}>
          {this.state.buttonText}
        </Button>
      </div>}
    </form>;
  }
}
/*<div className="list-with-right-controls">
        {this.state.authenticators.map(auth => <div className="item-with-right-controls" key={auth._id}>
          <div className="item-content">{auth.authname || <i>Unnamed</i>}</div>
          <span className="right-controls">
            <IconButton className="remove-btn" size="small"
                disableRipple disableTouchRipple
                onClick={() => this.doWebAuthn()}>
                <FingerprintIcon />
            </IconButton>
          </span>
        </div>)}
      </div>*/

export function LoginContainer(): React.ReactElement<any, any> {
  const [redirectPath, setRedirectPath] = React.useState("");
  const authContext = React.useContext<AuthContext>(AuthContext);
  const location = useLocation();
  const now = moment();

  async function onSuccess () {
    await authContext.finishedLogin();
    const urlParams = new URLSearchParams(
      location.search ? location.search.substring(1) : "");
    setRedirectPath(urlParams.get("next") || "/group");
  }

  if (redirectPath) {
    return <Navigate to={redirectPath} />;
  }
  return <div className="main-section-inner onboarding-welcome-container">
    <header className="welcome-heading">
      <div className="logo-img" />
    </header>
    <section className="login-form-container">
      <LoginForm onFinish={onSuccess} />
    </section>
    <section className="forgot-password-section">
      <p>
        <span>Forgot your Password? </span>
        <Link to="/pwrequest">Click Here</Link>.
      </p>
    </section>
    <footer className="fine-print">
      <p>
        <span>By logging you agree to SENTRIQS </span>
        <a href="https://www.sentriqs.com/privacy-policy">Privacy Policy</a>
        <span> and </span>
        <a href="https://www.sentriqs.com/terms-of-service">Terms of Service</a>
        <span>.</span>
      </p>
      <p className="copyright">
        &copy; {now.format("yyyy")} SENTRIQS. All Rights Reserved.
      </p>
    </footer>
  </div>;
}

interface LoginModalProps {
  open: boolean,
  onFinish: () => Promise<void>,
  onClose: () => void
}

export function LoginModal(props: LoginModalProps): React.ReactElement<any, any> {
  const loginForm = React.useRef<LoginForm>(null);

  function onConfirm () {
    loginForm.current?.doLogin();
  }

  return <Dialog open={props.open} title="Log In" loading={false}
      classNames="login-modal"
      onClose={props.onClose} onConfirm={onConfirm}>
    <LoginForm ref={loginForm}
      onFinish={props.onFinish}
      hideLogin={true} />
  </Dialog>;
}
